import Button from '@/common/components/base/button/Button';
import DashedTitle from '@/common/components/base/dashedTitle/DashedTitle';
import MobileInput from '@/common/components/base/input/mobileInput/MobileInput';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import FreeZonePlateInput from '@/common/components/base/input/plate/freeZonePlateInput/FreeZonePlateInput';
import { FreeZonePlateValue } from '@/common/components/base/input/plate/freeZonePlateInput/freeZonePlateInput.type';
import VehiclePlateInput from '@/common/components/base/input/plate/vehiclePlateInput/VehiclePlateInput';
import { plateValue } from '@/common/components/base/input/plate/vehiclePlateInput/vehiclePlateInput.type';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import usePlateDesignRequest from '@/common/hooks/usePlateDesignRequest';
import useVehicleTypeRequest from '@/common/hooks/useVehicleTypeRequest';
import { SimpleFilterFormType } from '@/modules/FileList/components/listFilterSection/simpleFilter/simpleFilterForm.type';
import { FILE_LIST_LINKS } from '@/modules/FileList/fileListLinks';
import useFilterQueryParams from '@/modules/FileList/hooks/useFilterQueryParams';
import { MyFilesListQueryKey } from '@/modules/FileList/queries/myFilesList/myFilesListQuery.type';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box } from 'styled-system/jsx';

const titleStyle = css({ mb: '1rem' });

const SimpleFilterForm = ({
  onCloseModal,
  isInNewDocumentPage = false,
}: {
  onCloseModal: () => void;
  isInNewDocumentPage?: boolean;
}) => {
  const navigate = useNavigate();
  const { resetQueryParams, fileListParams } = useFilterQueryParams();

  const {
    vehicleTypeQuery: { data: vehicleTypeOptions },
  } = useVehicleTypeRequest({
    enabled: true,
  });

  const {
    plateDesignQuery: { data: plateDesignOptions },
  } = usePlateDesignRequest({
    enabled: true,
  });

  const defaultForm = useMemo(() => {
    return {
      order_id: fileListParams?.order_id,
      phone_number: fileListParams?.phone_number,
      vehicle_type:
        fileListParams?.vehicle_type ??
        vehicleTypeOptions?.find((item) => item.enName === 'car'),
      plate_design:
        fileListParams?.plate_design ??
        plateDesignOptions?.find((item) => item.value === 'country'),
      plate: fileListParams?.plate,
    };
  }, []);

  const methods = useForm<SimpleFilterFormType>({
    defaultValues: defaultForm,
  });
  const formData = useWatch({ control: methods.control });

  const onSubmit = (params: SimpleFilterFormType) => {
    isInNewDocumentPage
      ? navigate(FILE_LIST_LINKS.myNewFilesWithParams(params), {
          replace: true,
        })
      : navigate(FILE_LIST_LINKS.myFilesWithParams(params), { replace: true });

    onCloseModal();
  };

  const reset = () => {
    methods.reset(defaultForm);
    resetQueryParams();
    onCloseModal();
  };

  useEffect(() => {
    methods.reset(defaultForm);
  }, []);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className={css({
          maxHeight: '80vh',
          height: '600px',
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem',
          justifyContent: 'space-between',
          overflowY: 'auto',
          mt: '2rem',
          scrollbarWidth: 'none',
        })}
      >
        <Box mt="2rem">
          <DashedTitle className={titleStyle} title="جستجو شناسه" />
          <TextInput direction="ltr" name="order_id" label="شناسه پرونده" />
          <DashedTitle className={titleStyle} title="جستجو تلفن" />
          <MobileInput
            defaultValue=""
            required={false}
            name="phone_number"
            label="شماره تلفن"
          />
          <DashedTitle className={titleStyle} title="جستجو پلاک" />
          <ModalInput
            options={vehicleTypeOptions}
            onChange={() => {
              methods.setValue(MyFilesListQueryKey.PLATE, undefined, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
              });
              methods.clearErrors(MyFilesListQueryKey.PLATE);
            }}
            name="vehicle_type"
            label="نوع وسیله‌نقلیه"
          />
          <ModalInput
            options={plateDesignOptions}
            onChange={() => {
              methods.setValue(MyFilesListQueryKey.PLATE, undefined, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
              });
              methods.clearErrors(MyFilesListQueryKey.PLATE);
            }}
            name="plate_design"
            label="طرح پلاک"
          />

          {formData[MyFilesListQueryKey.PLATE_DESIGN]?.value === 'country' &&
          formData[MyFilesListQueryKey.VEHICLE_TYPE]?.label !== 'موتورسیکلت' ? (
            <VehiclePlateInput
              onChange={(value) =>
                methods.setValue(MyFilesListQueryKey.PLATE, value, {
                  shouldDirty: true,
                  shouldTouch: true,
                })
              }
              value={formData[MyFilesListQueryKey.PLATE] as plateValue}
            />
          ) : (
            <FreeZonePlateInput
              key={`${formData[MyFilesListQueryKey.PLATE_DESIGN]?.value}${formData[MyFilesListQueryKey.VEHICLE_TYPE]?.value}`}
              requiredError={
                methods.formState.errors?.[MyFilesListQueryKey.PLATE]?.message
              }
              vehicle_plate_design={
                formData[MyFilesListQueryKey.PLATE_DESIGN]?.value ?? 'country'
              }
              vehicle_type_id={
                formData[MyFilesListQueryKey.VEHICLE_TYPE]?.value
                  ? +formData[MyFilesListQueryKey.VEHICLE_TYPE]?.value
                  : 1
              }
              onChange={(value) =>
                methods.setValue(MyFilesListQueryKey.PLATE, value)
              }
              value={formData[MyFilesListQueryKey.PLATE] as FreeZonePlateValue}
            />
          )}
          <Box
            width="100%"
            display="flex"
            position="sticky"
            bottom="0"
            bgColor="gray-4"
            justifyContent="space-between"
            gap="10px"
            mt="2rem"
          >
            <Button
              type="reset"
              onClick={reset}
              variant="outlined"
              color="common"
              width="50%"
            >
              حذف همه
            </Button>
            <Button disabled={!methods.formState.isDirty} width="50%">
              جستجو
            </Button>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};

export default SimpleFilterForm;
